import React, { useState } from 'react';
import { Loader } from 'lucide-react';
import PlayerLink from '../../../components/PlayerLink';
import { dynastyTokens } from '../../../services/colorService';
import { isPitcher, fmtSalary, acceptColor, positionLabel } from './optimizerUtils';

const colors = dynastyTokens.colors;

const OptimizerFlyout = ({
  player,           // Current roster player whose alternatives we're showing
  alternatives,     // Response from getOptimizerAlternatives — { alternatives: [...] } — or null
  loading,          // Boolean — API call in progress
  optimizerData,    // Full optimizer data — needed for meta.categories, meta.stat_field_mapping, category_gaps
  onClose,          // () => void
  onViewTrade,      // (recommendation) => void — opens trade detail modal
  onPickup,         // (faPlayer) => void — DD-RO-GAPS-0727 item 5: opens the add/drop modal for this FA
  selectedCandidate,  // item 4: the currently-highlighted FA (drives the gaps panel 'With selection' basis)
  onSelectCandidate,  // (faPlayer|null) => void — highlight a candidate row
}) => {
  const [activeTab, setActiveTab] = useState('free_agents');
  const [statsView, setStatsView] = useState('projected');

  const fpIsPitcher = isPitcher(player);
  const catType = fpIsPitcher ? 'pitching' : 'hitting';
  const catList = optimizerData?.meta?.categories?.[catType];
  const cats = Array.isArray(catList) ? catList : Object.keys(catList || {});
  const fieldMap = optimizerData?.meta?.stat_field_mapping || {};
  const alts = alternatives?.alternatives || [];
  // DD-RO-BUGS2-0724 Bug 7: the raw `player` (from getMyRosterCanonical) carries only stats.season,
  // so the Actual/Proj toggle was a no-op on the current-player box. The alternatives payload
  // returns the SAME player enriched with stats.projected (target_player); read the stat box from
  // it once it lands so the toggle actually changes the numbers.
  const detailPlayer = alternatives?.target_player || player;

  const currentRanks = {};
  if (optimizerData?.category_gaps) {
    for (const g of optimizerData.category_gaps) currentRanks[g.category] = g.current_rank;
  }

  const getPlayerStat = (p, cat) => {
    const dbField = fieldMap[cat];
    if (!dbField) return null;
    if (statsView === 'projected') {
      const proj = p.stats?.projected?.[catType];
      if (proj) return proj[cat] ?? proj[dbField] ?? null;
    }
    return p.stats?.season?.[catType]?.[dbField] ?? null;
  };

  const fmtStatVal = (val) => {
    if (val == null) return '—';
    return typeof val === 'number' && !Number.isInteger(val) ? val.toFixed(3).replace(/^0/, '') : String(val);
  };

  const rankColor = (rank) => {
    if (rank <= 3) return colors.success;
    if (rank <= 7) return colors.warning;
    return colors.error;
  };

  const ordSuffix = (n) => {
    const s = ['th','st','nd','rd'];
    const v = n % 100;
    return n + (s[(v-20)%10] || s[v] || s[0]);
  };

  return (
    <div style={{
      position: 'absolute', top: 12, right: 12, bottom: '25%', width: 390,
      background: colors.neutral[900], border: `1px solid ${colors.neutral[700]}`,
      borderRadius: 8,
      display: 'flex', flexDirection: 'column', zIndex: 30,
      boxShadow: '-4px 0 12px rgba(0,0,0,0.3)',
      overflow: 'hidden',
    }}>
      <style>{`
        .optimizer-flyout-scroll::-webkit-scrollbar { width: 4px; }
        .optimizer-flyout-scroll::-webkit-scrollbar-track { background: transparent; }
        .optimizer-flyout-scroll::-webkit-scrollbar-thumb { background: ${colors.neutral[700]}; border-radius: 2px; }
        .optimizer-flyout-scroll::-webkit-scrollbar-thumb:hover { background: ${colors.neutral[600]}; }
      `}</style>

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', padding: '10px 12px',
        borderBottom: `1px solid ${colors.neutral[700]}`, gap: 8,
      }}>
        <div
          onClick={onClose}
          style={{ cursor: 'pointer', color: colors.neutral[400], display: 'flex', alignItems: 'center' }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <span style={{ flex: 1, fontSize: 14, fontWeight: 700, color: colors.white, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          Alternatives for {player.info?.full_name || '—'}
        </span>
        <div
          onClick={onClose}
          style={{
            width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `1px solid ${colors.neutral[700]}`, borderRadius: 4, cursor: 'pointer',
            color: colors.neutral[400], fontSize: 14, fontWeight: 600,
          }}
        >x</div>
      </div>

      {/* Current player bar — the STAT BOX. DD-RO-BUGS2-0724 Bug 7: the Actual/Proj toggle now
          lives HERE, with the stats it governs (owner's note), and drives THIS box's numbers. */}
      <div style={{ background: colors.neutral[800], padding: '8px 14px', borderBottom: `1px solid ${colors.neutral[700]}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: colors.white, fontWeight: 600 }}>
            <PlayerLink mlbId={player.ids?.mlb} name={player.info?.full_name || '—'} style={{ color: colors.white, fontSize: 12, fontWeight: 600 }} />
            <span style={{ fontSize: 11, color: colors.neutral[400], fontWeight: 400 }}>
              {' '}· {positionLabel(player)} · {player.info?.mlb_team} · {fmtSalary(player)}
            </span>
          </div>
          <div style={{ display: 'flex', flexShrink: 0, background: colors.neutral[900], borderRadius: 3, overflow: 'hidden', border: `1px solid ${colors.neutral[700]}` }}>
            {['actual', 'projected'].map(sv => (
              <button
                key={sv}
                onClick={() => setStatsView(sv)}
                data-testid={`stats-toggle-${sv}`}
                aria-pressed={statsView === sv}
                style={{
                  padding: '3px 8px', fontSize: 10, fontWeight: 600,
                  border: 'none', cursor: 'pointer',
                  background: statsView === sv ? 'rgba(234,179,8,0.15)' : 'transparent',
                  color: statsView === sv ? '#eab308' : colors.neutral[500],
                  transition: 'all 0.15s',
                }}
              >{sv === 'actual' ? 'Actual' : 'Proj'}</button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
          {cats.map(cat => (
            <span key={cat} style={{ fontSize: 10 }}>
              <span style={{ color: colors.neutral[500] }}>{cat}</span>{' '}
              <span data-testid={`cur-stat-${cat}`} style={{ color: '#eab308', fontWeight: 600 }}>{fmtStatVal(getPlayerStat(detailPlayer, cat))}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', alignItems: 'center', borderBottom: `1px solid ${colors.neutral[700]}` }}>
        {[{ key: 'free_agents', label: 'Free agents' }, { key: 'trades', label: 'Trades' }].map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              flex: 1, padding: '8px 0', fontSize: 12, fontWeight: 600,
              border: 'none', cursor: 'pointer', background: 'transparent',
              color: activeTab === t.key ? '#eab308' : colors.neutral[500],
              borderBottom: activeTab === t.key ? '2px solid #eab308' : '2px solid transparent',
              transition: 'all 0.15s',
            }}
          >{t.label}</button>
        ))}
      </div>

      {/* Scrollable alternatives list */}
      <div className="optimizer-flyout-scroll" style={{ flex: 1, overflowY: 'auto', padding: '8px 10px' }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120, color: colors.neutral[400] }}>
            <Loader style={{ width: 18, height: 18, marginRight: 6, animation: 'spin 1s linear infinite' }} />
            Loading...
          </div>
        ) : activeTab === 'trades' ? (() => {
          // DD-RO-BUGS3-0724 Item 4: the Trades tab read optimizerData.recommendations filtered
          // by move_type==='trade' + estimated_package — a shape only a DEPRECATED builder ever
          // emitted, so it matched nothing (empty tab) and the click passed an incompatible object
          // to the detail modal (which reads `sequence` → opened nothing). The live trades are the
          // trade engine's sequences; consume them directly and hand the SAME sequence object
          // OptimizerTradeRecs feeds OptimizerTradeDetail (Rule 11 — no lossy adapter). Players in
          // a sequence's trade_package are FLAT (player_id/player_name/position/contract_salary).
          const playerPos = player.info?.position;
          const subjMlb = player.ids?.mlb;
          // DD-RO-TRADEDETAIL-0725 (d): subject-tethered at the SOURCE (I2 enforced at generation, not by a
          // display filter). Two legitimate shapes, both keeping the subject in a leg:
          //   (i)  he is the give-up anchor — the subject-anchored send trades the backend generated for
          //        THIS player (alternatives.subject_trades, from get_targeted_trades(player,'send')); every
          //        one has him in user_sends by construction.
          //   (ii) another asset is traded for an upgrade at his slot and HE is the cut/displaced piece —
          //        team-wide sequences where he is a follow-up drop.
          // The involvesSubject check is now belt-and-suspenders (both sources are subject-tethered by
          // construction), not the load-bearing gate it was.
          const positive = (seq) => (seq.total_standings_gain > 0 || seq.trade_standings_gain > 0);
          const sendSeqs = (alternatives?.subject_trades || []).filter(positive);                 // shape (i)
          const cutSeqs = (optimizerData?.trade_engine?.trade_sequences || []).filter(seq =>       // shape (ii)
            positive(seq) && (seq.follow_up_moves || []).some(m => m.player_out?.player_id === subjMlb));
          const seen = new Set(sendSeqs.map(s => s.sequence_id));
          const tradeSeqs = [...sendSeqs, ...cutSeqs.filter(s => !seen.has(s.sequence_id))];

          if (tradeSeqs.length === 0) {
            // DD-RO-BUGS4-0725 B6 — the empty result is INFORMATION (owner ruling). No beneficial
            // trade for this player means the move is to replace him from free agency; say that and
            // point at the FA alternatives for the same slot (already loaded in `alts`).
            // DD-RO-CLASSAUDIT-0725 gate flip — honest empty state: if the output gate WITHHELD trades,
            // say that instead of "none found" (a withheld rec is invisible by design).
            const name = player.info?.full_name || 'this player';
            const withheld = alternatives?.subject_trades_gate?.withheld || 0;
            return (
              <div style={{ textAlign: 'center', color: colors.neutral[600], fontSize: 12, padding: 24 }}>
                <div style={{ fontWeight: 600, color: colors.neutral[700], marginBottom: 6 }}>
                  {withheld > 0
                    ? `${withheld} trade${withheld === 1 ? '' : 's'} involving ${name} were withheld by a consistency check.`
                    : `No trades involving ${name} found.`}
                </div>
                <div style={{ marginBottom: 12 }}>
                  No value-justified trade sends or replaces him right now — the move is to replace him from free agency.
                </div>
                {alts.length > 0 ? (
                  <button
                    onClick={() => setActiveTab('free_agents')}
                    style={{ background: 'none', border: `1px solid ${colors.primary || colors.neutral[400]}`,
                             color: colors.primary || colors.neutral[700], borderRadius: 6,
                             padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}
                  >
                    See {alts.length} free-agent {playerPos} alternative{alts.length > 1 ? 's' : ''} →
                  </button>
                ) : (
                  <div>No free-agent alternatives at {playerPos} either.</div>
                )}
              </div>
            );
          }

          return tradeSeqs.map((seq, idx) => {
            const pkg = seq.trade_package || {};
            const recvPlayer = (pkg.user_receives?.players || [])[0];
            const delta = pkg.net_value ?? 0;
            const deltaColor = delta > 0 ? colors.success : delta < 0 ? colors.error : colors.neutral[500];
            const acceptance = pkg.acceptance_probability;

            return (
              <div
                key={seq.sequence_id || idx}
                data-testid="flyout-trade-card"
                style={{
                  border: `1px solid ${colors.neutral[700]}`, borderRadius: 4,
                  borderLeft: `3px solid ${colors.warning}`,
                  background: colors.neutral[800], padding: '8px 10px',
                  marginBottom: 5, transition: 'border-color 0.15s', cursor: 'pointer',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(234,179,8,0.5)'}
                onMouseLeave={e => { e.currentTarget.style.borderColor = colors.neutral[700]; e.currentTarget.style.borderLeftColor = colors.warning; }}
                onClick={() => onViewTrade && onViewTrade(seq)}
              >
                {pkg.trade_type === 'three_team' && (
                  <div style={{ fontSize: 10, color: colors.neutral[500], marginBottom: 3 }}>3-team cycle</div>
                )}
                {/* Player + delta */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ flex: 1, overflow: 'hidden' }}>
                    {recvPlayer && (
                      <>
                        <span style={{ fontSize: 12, fontWeight: 600, color: colors.white }}>
                          <PlayerLink mlbId={recvPlayer.player_id} name={recvPlayer.player_name || '—'} style={{ color: colors.white, fontSize: 12, fontWeight: 600 }} />
                        </span>
                        <span style={{ fontSize: 11, color: colors.neutral[400] }}>
                          {' '}· {positionLabel(recvPlayer)}{recvPlayer.contract_salary != null ? ` · $${recvPlayer.contract_salary}` : ''}
                        </span>
                      </>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, marginLeft: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: deltaColor }}>
                      {delta > 0 ? `+${delta}` : delta}
                    </span>
                  </div>
                </div>
                {/* Acceptance + View button */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                  {acceptance != null && (
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3,
                      background: `${acceptColor(acceptance)}20`,
                      color: acceptColor(acceptance),
                    }}>
                      {Math.round(acceptance * 100)}% likely
                    </span>
                  )}
                  <button style={{
                    border: `1px solid ${colors.neutral[700]}`, color: colors.neutral[400],
                    fontSize: 10, padding: '2px 8px', borderRadius: 3,
                    background: 'transparent', cursor: 'pointer',
                  }}>View trade</button>
                </div>
              </div>
            );
          });
        })() : alts.length === 0 ? (
          <div style={{ textAlign: 'center', color: colors.neutral[600], fontSize: 12, padding: 24 }}>
            No improvements available at this position
          </div>
        ) : alts.map((alt, idx) => {
          const ap = alt.player;
          const delta = alt.score?.weighted_score ?? alt.score?.total_points_delta ?? 0;
          const deltaColor = delta > 0 ? colors.success : delta < 0 ? colors.error : colors.neutral[500];
          const catDeltas = alt.score?.category_deltas || [];
          const deltaMap = {};
          for (const cd of catDeltas) deltaMap[cd.category] = cd;

          // DD-RO-GAPS-0727 item 4: rows are selectable — clicking highlights the candidate and drives the
          // gaps panel's 'With selection' basis (its projected standings with this FA). Click again to clear.
          const isSelected = selectedCandidate?.ids?.mlb != null && selectedCandidate.ids.mlb === ap.ids?.mlb;
          return (
            <div
              key={ap.ids?.mlb || idx}
              onClick={() => onSelectCandidate && onSelectCandidate(isSelected ? null : ap)}
              style={{
                border: `1px solid ${isSelected ? 'rgba(234,179,8,0.9)' : colors.neutral[700]}`, borderRadius: 4,
                background: isSelected ? 'rgba(234,179,8,0.08)' : colors.neutral[800], padding: '8px 10px',
                marginBottom: 5, transition: 'border-color 0.15s', cursor: 'pointer',
              }}
              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = 'rgba(234,179,8,0.5)'; }}
              onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = colors.neutral[700]; }}
            >
              {/* Line 1: Player info + delta + button */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ flex: 1, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: colors.white }}>
                    <PlayerLink mlbId={ap.ids?.mlb} name={ap.info?.full_name || '—'} style={{ color: colors.white, fontSize: 12, fontWeight: 600 }} />
                  </span>
                  <span style={{ fontSize: 11, color: colors.neutral[400] }}>
                    {' '}· {positionLabel(ap)} · {ap.info?.mlb_team} · {(() => {
                      const fin = ap.league_context?.financial;
                      const sal = fin?.contract_salary;
                      const price = sal > 0 ? sal : fin?.baseline_price;
                      return price != null ? `$${Math.round(price)}` : '';
                    })()}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, marginLeft: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: deltaColor }}>
                    {delta > 0 ? `+${delta}` : delta}
                  </span>
                  {/* DD-RO-GAPS-0727 item 5: wired. Opens the add/drop modal (PlayerModalContext) for this
                      FA — the same flow (add-with-required-drop → POST /add-player) used everywhere else and
                      mirrored by MobileFASwapSheet. Was hard-disabled since DD-RO-BUGS2-0724 (no desktop
                      flow existed); the flow exists, so the button is now live. */}
                  <button
                    onClick={(e) => { e.stopPropagation(); onPickup && onPickup(ap); }}
                    title="Pick up this free agent"
                    style={{
                      border: `1px solid ${colors.neutral[500]}`, color: 'rgba(234,179,8,0.95)',
                      fontSize: 10, padding: '2px 8px', borderRadius: 3,
                      background: 'transparent', cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(234,179,8,0.12)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >Pick up</button>
                </div>
              </div>

              {/* Line 2: Category stats */}
              <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                {cats.map(cat => {
                  const val = getPlayerStat(ap, cat);
                  const curVal = getPlayerStat(player, cat);
                  const isBetter = val != null && curVal != null && (
                    ['ERA', 'WHIP', 'BB'].includes(cat) ? val < curVal : val > curVal
                  );
                  return (
                    <span key={cat} style={{ fontSize: 10 }}>
                      <span style={{ color: colors.neutral[500] }}>{cat}</span>{' '}
                      <span style={{ color: isBetter ? colors.success : colors.neutral[400], fontWeight: isBetter ? 600 : 400 }}>
                        {fmtStatVal(val)}
                      </span>
                    </span>
                  );
                })}
              </div>

              {/* Line 3: Rank impact */}
              {catDeltas.length > 0 && (
                <div style={{ borderTop: '1px solid rgba(68,64,60,0.4)', marginTop: 5, paddingTop: 5, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  {/* DD-RO-GAPS-CLUSTER-0728 4.2a: show the RAW projected stat delta next to the rank move, so
                      a rank flip has context and never reads as an absurd claim (the impact is the team's
                      projected standings change from the swap \u2014 lineup effects included \u2014 not a naive
                      player-vs-player HR comparison). */}
                  <span style={{ fontSize: 10, color: colors.neutral[600] }}>Projected impact:</span>
                  {cats.map(cat => {
                    const cd = deltaMap[cat];
                    if (!cd) return null;
                    const before = cd.rank_before;
                    const after = cd.rank_after;
                    const sd = cd.stat_delta;
                    // DD-RO-SELECTION-LOGIC-0728 Bug 2.2: counting-stat totals show as WHOLE numbers (R +4.7 → +5,
                    // SB +18.4 → +18) — a fractional projected count reads as false precision. Ratio stats
                    // (AVG/OPS/ERA/WHIP, |sd| < 1) keep their 3-decimal form. Rank arrows are unchanged.
                    const sdStr = sd == null ? null
                      : (Math.abs(sd) < 1 ? sd.toFixed(3).replace(/^(-?)0\./, '$1.')
                                          : (sd > 0 ? '+' : '') + Math.round(sd));
                    const sdColor = sd > 0 ? colors.success : sd < 0 ? colors.error : colors.neutral[500];
                    return (
                      <span key={cat} style={{ fontSize: 10 }} data-testid="flyout-impact-chip">
                        <span style={{ color: colors.neutral[500] }}>{cat}</span>{' '}
                        {sdStr != null && <span style={{ color: sdColor }} data-testid="flyout-impact-stat">{sdStr}</span>}{' '}
                        {before === after
                          ? <span style={{ color: rankColor(after) }}>{ordSuffix(after)}</span>
                          : (
                            <>
                              <span style={{ color: rankColor(before) }}>{ordSuffix(before)}</span>
                              <span style={{ color: colors.neutral[600] }}>{'\u2192'}</span>
                              <span style={{ color: rankColor(after) }}>{ordSuffix(after)}</span>
                            </>
                          )}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OptimizerFlyout;
